// Tenant company info — editable by all staff roles (owner/admin/back_office).
// Values feed the customer-facing quote PDF (header logo + address). Labor rates
// (a separate section) stay owner/admin only.
// (Sales tax was removed in migration 0048 — no tax-rate field.)

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Upload as UploadIcon } from 'lucide-react';
import {
  getMe,
  patchTenantSettings,
  uploadTenantLogo,
} from '../api/client.js';
import { Card, CardContent } from './ui/card.js';
import { Badge } from './ui/badge.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { downscaleLogo } from '../lib/downscale-image.js';

export function CompanyInfoSection() {
  const queryClient = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: getMe });
  const tenant = me?.tenant ?? null;
  // Company info (name/address/phone/email) + logo are editable by all staff
  // roles. Labor rates (a separate section) stay owner/admin only.
  const canEdit =
    me?.role === 'owner' || me?.role === 'admin' || me?.role === 'back_office';

  // Local draft state — initialized from server data; user edits and
  // hits Save. We don't auto-save because users want to bulk-edit the
  // contact block, not commit per keystroke.
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!tenant) return;
    setName(tenant.name);
    setAddress(tenant.address ?? '');
    setPhone(tenant.phone ?? '');
    setEmail(tenant.email ?? '');
  }, [tenant]);

  const saveMutation = useMutation({
    mutationFn: () =>
      patchTenantSettings({
        name: name.trim(),
        address: address.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const logoMutation = useMutation({
    // Downscale to ≤150px BEFORE upload so the quote PDF embeds a tiny logo
    // (oversized logos blow the worker's PDF-generate CPU budget → 1102).
    mutationFn: async (file: Blob) => uploadTenantLogo(await downscaleLogo(file)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const isDirty =
    !!tenant &&
    (name !== tenant.name ||
      (address || null) !== (tenant.address ?? null) ||
      (phone || null) !== (tenant.phone ?? null) ||
      (email || null) !== (tenant.email ?? null));

  return (
    <Card>
        <CardContent className="space-y-4 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-secondary/40">
              {tenant?.logoUrl ? (
                <img
                  src={tenant.logoUrl}
                  alt="Company logo"
                  className="h-full w-full object-contain"
                />
              ) : (
                <Building2 className="h-8 w-8 text-taupe" />
              )}
            </div>
            <div className="flex-1 space-y-2">
              <div className="text-xs text-muted-foreground">
                Logo appears on quote PDFs. PNG, JPEG, or WebP, up to 2 MB.
              </div>
              {canEdit && (
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium hover:bg-accent/40">
                  <UploadIcon className="h-3.5 w-3.5" />
                  {logoMutation.isPending ? 'Uploading…' : 'Upload logo'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    disabled={logoMutation.isPending}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) logoMutation.mutate(f);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
              {logoMutation.error && (
                <div className="text-xs text-destructive">
                  {logoMutation.error instanceof Error
                    ? logoMutation.error.message
                    : 'Upload failed'}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="eyebrow">Signed in as</div>
              <div className="text-sm font-semibold text-foreground">
                {me?.email ?? '—'}
              </div>
              {me?.role && (
                <Badge
                  variant={me.role === 'owner' ? 'default' : 'secondary'}
                  className="mt-1"
                >
                  {me.role.replace('_', ' ')}
                </Badge>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="company-name" className="eyebrow mb-1.5 block">
                Company name *
              </label>
              <Input
                id="company-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canEdit || saveMutation.isPending}
              />
            </div>
            <div>
              <label htmlFor="company-address" className="eyebrow mb-1.5 block">
                Address
              </label>
              <Input
                id="company-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, Asheville NC 28801"
                disabled={!canEdit || saveMutation.isPending}
              />
            </div>
            <div>
              <label htmlFor="company-phone" className="eyebrow mb-1.5 block">
                Phone
              </label>
              <Input
                id="company-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="828-555-0100"
                disabled={!canEdit || saveMutation.isPending}
              />
            </div>
            <div>
              <label htmlFor="company-email" className="eyebrow mb-1.5 block">
                Email
              </label>
              <Input
                id="company-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@example.com"
                disabled={!canEdit || saveMutation.isPending}
              />
            </div>
          </div>

          {saveMutation.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {saveMutation.error instanceof Error
                ? saveMutation.error.message
                : 'Save failed'}
            </div>
          )}

          {canEdit && (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  if (!tenant) return;
                  setName(tenant.name);
                  setAddress(tenant.address ?? '');
                  setPhone(tenant.phone ?? '');
                  setEmail(tenant.email ?? '');
                  saveMutation.reset();
                }}
                disabled={!isDirty || saveMutation.isPending}
              >
                Reset
              </Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!isDirty || !name.trim() || saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
  );
}
